import React, {useContext, useState} from 'react'
import PropTypes from "prop-types";
import GridBlock from "components/GridBlock";
import Typography from "@material-ui/core/Typography";
import GeneralInfoExpansionPanelTitle from "./GeneralInfoExpansionPanelTitle";
import Tr from "translations/Translations";
import GeneralInfoEntry from "./GeneralInfoEntry";
import ExpansionPanel from "components/ExpansionPanel";
import {PriceTypeLabel} from "constants/PriceTypes";
import {isEuropeProcedure} from "constants/ProcedureType";
import formatAmountNumber from "libs/formatAmountNumber";
import moment from "moment";
import DateFormats from "constants/DateFormats";
import {VALUES as ProcedureStatus} from "constants/ProcedureStatus";
import {jedzTranslations} from "pages/purchaser/MyProcedure/scenes/part1/panels/demand/dataEdit/fieldsOptions/jedz.values";
import {PrimaryTenderVariantTr} from "constants/PrimaryTenderVariant";
import OfferStageTranslationsContext from "../../../../../../../../../context/OfferStageTranslationsContext";
import {makeStyles} from "@material-ui/core";

const useStyles = makeStyles(theme => ({
    textarea: {
        whiteSpace: 'pre-wrap'
    }
}));

function GeneralInfoBasicData(
    {
        panelsClassesObject,
        generalInfoBasicData,
        demandHeader
    }) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const stageTr = useContext(OfferStageTranslationsContext);
    return (
        <ExpansionPanel
            classesObj={panelsClassesObject}
            title={
                <GeneralInfoExpansionPanelTitle
                    subject={Tr("basic_data")}
                    toggleThread={() => setExpanded(x => !x)}
                    expanded={expanded}
                />
            }
            expanded={expanded}
            onChange={(e, isExpanded) => setExpanded(isExpanded)}

        >
            <GridBlock column>
                <GeneralInfoEntry label={Tr('contact_person')}>
                    <Typography>
                        {generalInfoBasicData.contactPerson}
                        <br/>
                        {generalInfoBasicData.purchaserEmail}
                        <br/>
                        {generalInfoBasicData.contactPersonPhone}
                    </Typography>
                </GeneralInfoEntry>

                <GeneralInfoEntry label={Tr('demand_type')}>
                    <Typography>
                        {generalInfoBasicData.procedureTypeTranslated}
                    </Typography>
                </GeneralInfoEntry>

                <GeneralInfoEntry label={Tr('demand_realization_type')}>
                    <Typography>
                        {generalInfoBasicData.realizationTypeTranslated}
                    </Typography>
                </GeneralInfoEntry>

                {!!generalInfoBasicData.description &&
                <GeneralInfoEntry label={Tr('demand_description')}>
                    <Typography className={classes.textarea}>
                        {generalInfoBasicData.description}
                    </Typography>
                </GeneralInfoEntry>
                }

                {!!generalInfoBasicData.primaryTenderVariant &&
                <GeneralInfoEntry label={Tr('primary_tender_variant')}>
                    <Typography>
                        {PrimaryTenderVariantTr[generalInfoBasicData.primaryTenderVariant]}
                    </Typography>
                </GeneralInfoEntry>
                }

                {!!generalInfoBasicData.orderType &&
                <GeneralInfoEntry label={Tr('order_type')}>
                    <Typography>
                        {generalInfoBasicData.orderType}
                    </Typography>
                </GeneralInfoEntry>
                }

                {!generalInfoBasicData.passiveOfferForm &&
                <GeneralInfoEntry label={Tr('offer_price_type')}>
                    <Typography>
                        {PriceTypeLabel(generalInfoBasicData.priceType)} (PLN)
                    </Typography>
                </GeneralInfoEntry>
                }

                {generalInfoBasicData.canCreateAuction !== null &&
                    <GeneralInfoEntry label={Tr('offer_electronic_auction')}>
                        <Typography>
                            {Tr(generalInfoBasicData.canCreateAuction ? 'yes' : 'no')}
                        </Typography>
                    </GeneralInfoEntry>
                }

                {generalInfoBasicData.digitalSign !== null &&
                    <GeneralInfoEntry
                        label={stageTr(isEuropeProcedure(generalInfoBasicData.procedureType) ? "electronic_signature_question" : "electronic_signature_national_question")}>
                        <Typography>
                            {Tr(generalInfoBasicData.digitalSign ? 'yes' : 'no')}
                        </Typography>
                    </GeneralInfoEntry>
                }

                {generalInfoBasicData.requiredJEDZ !== null &&
                    <GeneralInfoEntry
                        label={Tr('enable_JEDZ_question')}>
                        <Typography>
                            {jedzTranslations(generalInfoBasicData.requiredJEDZ)}
                        </Typography>
                    </GeneralInfoEntry>
                }

                {!generalInfoBasicData.passiveOfferForm &&
                    <GeneralInfoEntry
                        label={Tr('guarantee')}
                        collapsible={generalInfoBasicData.guaranteeList && generalInfoBasicData.guaranteeList.length > 3}
                    >

                        {generalInfoBasicData.guaranteeList === null ?
                            (
                                generalInfoBasicData.guaranteeStatus === null || generalInfoBasicData.guaranteeStatus === 'not_specified'
                                    ? <Typography>{Tr('guarantee_not_specified')}</Typography>
                                    : <Typography>{Tr('guarantee_not_required')}</Typography>
                            )
                            :
                            <>
                                {generalInfoBasicData.dealingsCount === 1 ?
                                    <>
                                        {generalInfoBasicData.guaranteeList.length > 0 &&
                                            <Typography>{formatAmountNumber(generalInfoBasicData.guaranteeList[0].value)} PLN</Typography>}
                                    </>
                                    :
                                    <>
                                        {generalInfoBasicData.guaranteeList.map(x => (
                                            <Typography
                                                key={x.identity}>{Tr("part_x", {x: x.ordinalNumber})}: {formatAmountNumber(x.value)} PLN</Typography>))}

                                    </>
                                }
                            </>
                        }
                    </GeneralInfoEntry>
                }


                <GeneralInfoEntry label={Tr("demand_publish_date")}>
                    <Typography>
                        {moment(generalInfoBasicData.procedureStartDate).format(DateFormats.DATETIME_FORMAT)}
                    </Typography>
                </GeneralInfoEntry>

                <GeneralInfoEntry label={generalInfoBasicData.stageTypeTranslated}>
                    {generalInfoBasicData.stageEndDate === null ?
                        <Typography>{Tr('offer_end_date_neg_dialog')}: {Tr('according_to_the_documentation')}</Typography>
                        :
                        <Typography>
                            {Tr("offer_end_date")}: {moment(generalInfoBasicData.stageEndDate).format(DateFormats.DATETIME_FORMAT)}
                        </Typography>
                    }
                    <Typography>
                        {Tr("offer_open_date")}: {generalInfoBasicData.openOfferDate === null ? Tr('according_to_the_documentation') : moment(generalInfoBasicData.openOfferDate).format(DateFormats.DATETIME_FORMAT)}
                    </Typography>
                    {generalInfoBasicData.stageSecondEndDate &&
                        <Typography>
                            {Tr("offer_second_end_date")}: {moment(generalInfoBasicData.stageSecondEndDate).format(DateFormats.DATETIME_FORMAT)}
                        </Typography>
                    }
                </GeneralInfoEntry>

                {generalInfoBasicData.demandFinishDate !== null &&
                    <GeneralInfoEntry label={Tr("demand_finish_date")}>
                        {demandHeader.status === ProcedureStatus.CANCELLED ?
                            <Typography>{Tr('procedure_status_6')}</Typography>
                            :
                            <Typography>
                                {moment(generalInfoBasicData.demandFinishDate).format(DateFormats.DATETIME_FORMAT)}
                            </Typography>
                        }
                    </GeneralInfoEntry>
                }
            </GridBlock>
        </ExpansionPanel>
    )
}

GeneralInfoBasicData.propTypes = {
    generalInfoBasicData: PropTypes.object.isRequired,
    demandHeader: PropTypes.object.isRequired,
}

export default GeneralInfoBasicData;